import { useCallback, useMemo, useRef, useState } from 'react';
import { CategoryNode } from '../growCategoryRestriction.types';
import {
    collectLeafNodes,
    hasPendingRestrictionChanges,
    insertIntoRestrictionTree,
    mergeDiscoveredIds,
} from '../growCategoryRestriction.helpers';

/**
 * Shared restriction checkbox + Active Restrictions tree state.
 * Soft-merges API discoveries without wiping in-progress edits.
 */
export const useRestrictionState = () => {
    const [restrictedIds, setRestrictedIds] = useState<Set<number>>(new Set());
    const [baselineRestrictedIds, setBaselineRestrictedIds] = useState<Set<number>>(new Set());
    const [restrictionTree, setRestrictionTree] = useState<CategoryNode[]>([]);
    const restrictionTreeRef = useRef(restrictionTree);
    restrictionTreeRef.current = restrictionTree;

    const hasRestrictionChanges = useMemo(
        () => hasPendingRestrictionChanges(restrictionTree, restrictedIds, baselineRestrictedIds),
        [restrictionTree, restrictedIds, baselineRestrictedIds]
    );

    const resetRestrictionState = useCallback(() => {
        setRestrictedIds(new Set());
        setBaselineRestrictedIds(new Set());
        setRestrictionTree([]);
    }, []);

    /**
     * Soft-merge API restricted IDs into checkbox state.
     * Pending leaf edits win over API values.
     */
    const discoverRestrictedIds = useCallback((newIds: Set<number>) => {
        if (newIds.size === 0) return;

        const pendingLeafIds = new Set(
            collectLeafNodes(restrictionTreeRef.current).map((n) => n.categoryId)
        );

        setRestrictedIds((prev) => mergeDiscoveredIds(prev, newIds, pendingLeafIds));
        setBaselineRestrictedIds((prev) => mergeDiscoveredIds(prev, newIds, pendingLeafIds));
    }, []);

    /**
     * Bi-directional checkbox toggle.
     * - ancestors provided → from CategoryHierarchy (add path to restriction tree)
     * - ancestors omitted  → from ActiveRestrictions (checkbox-only update)
     */
    const toggleRestrict = useCallback((node: CategoryNode, checked: boolean, ancestors?: CategoryNode[]) => {
        setRestrictedIds((prev) => {
            const next = new Set(prev);
            if (checked) next.add(node.categoryId);
            else next.delete(node.categoryId);
            return next;
        });

        if (ancestors !== undefined) {
            setRestrictionTree((prev) => insertIntoRestrictionTree(prev, node, ancestors));
        }
    }, []);

    const getPendingLeafNodes = useCallback(() => collectLeafNodes(restrictionTreeRef.current), []);

    return {
        restrictedIds,
        baselineRestrictedIds,
        restrictionTree,
        hasRestrictionChanges,
        resetRestrictionState,
        discoverRestrictedIds,
        toggleRestrict,
        getPendingLeafNodes,
    };
};
